import { useLocation } from "react-router-dom";
import queryString from "query-string";
import axios from "axios";
import { useEffect, useState } from "react";
import { url } from "../../config";
import exportFromJSON from "export-from-json";
import {
  FileSpreadsheet,
  FileText,
  FileCode,
  Download,
  X,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";

const ExportModal = ({ isOpen = false, onClose = () => {}, tableName }) => {
  const fileName = `${tableName || "data"}-export-${new Date().toISOString().split("T")[0]}`;
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState("csv"); // csv, xls, json
  const [fetchError, setFetchError] = useState(false);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const normalizeParsedParams = (parsed) => {
    const result = {};
    for (const [key, value] of Object.entries(parsed)) {
      const match = key.match(/^(\w+)\[(\w+)\]$/);
      if (match) {
        const [, parent, child] = match;
        if (!result[parent]) result[parent] = {};
        result[parent][child] = value;
      } else {
        result[key] = value;
      }
    }
    return result;
  };

  const fetchExportData = () => {
    const rawFilters = queryString.parse(location.search, {
      arrayFormat: "bracket",
    });
    const filters = normalizeParsedParams(rawFilters);

    setLoading(true);
    setFetchError(false);

    axios
      .post(`${url}/model/${tableName}/export`, { ...filters })
      .then((res) => {
        setListData(res?.data?.data || []);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setFetchError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatExportValue = (value) => {
    if (value === null || value === undefined) return "--";
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch (err) {
        return "--";
      }
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return value;
  };

  const handleDownload = () => {
    const rows = listData?.rows || (Array.isArray(listData) ? listData : []);

    if (!rows.length) return;

    const headings = Object.keys(rows[0]);
    const jsonArray = rows.map((row) => {
      const formattedRow = {};
      headings.forEach((heading) => {
        formattedRow[heading] = formatExportValue(row[heading]);
      });
      return formattedRow;
    });

    let exportType = exportFromJSON.types.csv;
    if (selectedFormat === "xls") exportType = exportFromJSON.types.xls;
    if (selectedFormat === "json") exportType = exportFromJSON.types.json;

    exportFromJSON({ data: jsonArray, fileName, exportType });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      fetchExportData();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const rowsCount =
    listData?.rows?.length || (Array.isArray(listData) ? listData.length : 0);

  const formatOptions = [
    {
      id: "csv",
      label: "CSV",
      desc: "Standard comma-separated format",
      icon: FileText,
    },
    {
      id: "xls",
      label: "Excel (XLS)",
      desc: "Spreadsheet viewer format",
      icon: FileSpreadsheet,
    },
    {
      id: "json",
      label: "JSON",
      desc: "Raw structured data object",
      icon: FileCode,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 transition-all"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full overflow-hidden transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
              <Download size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">Export Data</h3>
              <p className="text-xs text-slate-500 capitalize">
                Table:{" "}
                <span className="font-semibold text-slate-700">
                  {tableName}
                </span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Status Banner */}
          {loading ? (
            <div className="flex items-center gap-3 p-3.5 bg-blue-50/70 border border-blue-100 rounded-xl text-blue-700 text-sm">
              <Loader2 className="w-4 h-4 animate-spin shrink-0" />
              <span>Fetching matching records for export...</span>
            </div>
          ) : fetchError ? (
            <div className="flex items-center gap-3 p-3.5 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>Failed to fetch records. Please try again.</span>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-xl">
              <span className="text-sm font-medium text-slate-600">
                Records Available:
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200/60 rounded-lg text-xs font-bold">
                <CheckCircle2 size={13} />
                {rowsCount} {rowsCount === 1 ? "row" : "rows"}
              </span>
            </div>
          )}

          {/* Format Selector */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">
              Select Output Format
            </label>
            <div className="grid grid-cols-1 gap-2">
              {formatOptions.map((item) => {
                const Icon = item.icon;
                const isSelected = selectedFormat === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedFormat(item.id)}
                    className={`flex items-center justify-between p-3.5 rounded-xl border transition-all text-left ${
                      isSelected
                        ? "border-blue-600 bg-blue-50/30 text-blue-900 ring-2 ring-blue-500/20"
                        : "border-slate-200 hover:border-slate-300 bg-white text-slate-700"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${isSelected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}
                      >
                        <Icon size={18} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold">
                          {item.label}
                        </div>
                        <div className="text-xs text-slate-400">
                          {item.desc}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? "border-blue-600 bg-blue-600" : "border-slate-300"}`}
                    >
                      {isSelected && (
                        <div className="w-1.5 h-1.5 bg-white rounded-full" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50/50 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-xl text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={loading || rowsCount === 0 || fetchError}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 transition-all active:scale-[0.98]"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Preparing...</span>
              </>
            ) : (
              <>
                <Download size={16} />
                <span>Download File</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
